import { useState } from "react";
import { Loader2, Sparkles, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Panel } from "@/components/Panel";
import { http, useResource, formatError, emitRefresh } from "@/lib/api";

const INTENTS = [
  { v: "", l: "All" },
  { v: "complaint", l: "Complaints" },
  { v: "quote_request", l: "Quote requests" },
  { v: "shipment_status", l: "Shipment status" },
  { v: "product_question", l: "Product questions" },
  { v: "other", l: "Other" },
  { v: "unsorted", l: "Unsorted" },
];
const TONE = { complaint: "text-red-300 border-red-500/50", quote_request: "text-emerald-300 border-emerald-500/40", shipment_status: "text-amber-300 border-amber-500/40", product_question: "text-sky-300 border-sky-500/40" };
const label = (v) => INTENTS.find((i) => i.v === v)?.l || v;

const Conversation = ({ c }) => {
  const [msgs, setMsgs] = useState(null);
  const toggle = () => (msgs ? setMsgs(null) : http.get(`/website/conversations/${c.session_id}`).then((r) => setMsgs(r.data)));
  return (
    <li className="border-b border-white/10 py-3" data-testid={`conversation-${c.session_id}`}>
      <button onClick={toggle} className="w-full flex items-start gap-3 text-left group" data-testid={`conversation-toggle-${c.session_id}`}>
        <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-[0.15em] px-3 py-1 rounded-full border ${TONE[c.intent] || "text-white/70 border-white/20"}`} data-testid={`conversation-intent-${c.session_id}`}>{label(c.intent)}</span>
        <span className="flex-1 text-sm text-slate-200 line-clamp-2">{c.first}</span>
        <span className="font-mono-hud text-[11px] text-slate-500 shrink-0">{c.lead ? `${c.lead} · ` : ""}{c.count} msg · {new Date(c.last_at).toLocaleDateString()}</span>
        <ChevronDown size={14} className={`text-slate-500 transition-transform ${msgs ? "rotate-180" : ""}`} />
      </button>
      {msgs && (
        <div className="mt-3 ml-2 space-y-2 border-l border-white/10 pl-4" data-testid={`conversation-transcript-${c.session_id}`}>
          {msgs.map((m) => (
            <div key={m.id || m.created_at} className="text-sm"><span className="hud-label !text-[9px] mr-2">{m.role === "user" ? "Buyer" : "Jarvis"}</span><span className={m.role === "user" ? "text-slate-200" : "text-slate-400"}>{m.content}</span></div>
          ))}
        </div>
      )}
    </li>
  );
};

export const WebsiteChats = () => {
  const [intent, setIntent] = useState("");
  const [data, reload] = useResource(`/website/conversations${intent ? `?intent=${intent}` : ""}`);
  const [busy, setBusy] = useState(false);
  const sort = async () => {
    setBusy(true);
    try { const { data: r } = await http.post("/website/conversations/classify"); toast.success(`${r.classified} message(s) sorted`); reload(); emitRefresh(); }
    catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  const counts = data?.counts || {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return (
    <Panel label="Sorted by intent · Jev" title="Website Chats" testId="website-chats-panel" className="mb-6"
      right={counts.unsorted > 0 && <button className="hud-btn hud-btn-ghost !py-1.5" onClick={sort} disabled={busy} data-testid="website-chats-sort">{busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Sort {counts.unsorted} unsorted</button>}>
      <div className="flex flex-wrap gap-2 mb-4">
        {INTENTS.filter((i) => !i.v || counts[i.v]).map((i) => (
          <button key={i.v} onClick={() => setIntent(i.v)} data-testid={`intent-filter-${i.v || "all"}`}
            className={`text-xs px-4 py-1.5 rounded-full border transition-colors ${intent === i.v ? "bg-[#ffdcb8] text-[#2b0d02] border-transparent" : "border-white/15 text-slate-300 hover:border-white/40"}`}>
            {i.l} <span className="opacity-60 ml-1">{i.v ? counts[i.v] : total}</span>
          </button>
        ))}
      </div>
      <ul className="max-h-[420px] overflow-y-auto hud-scroll pr-2">
        {(data?.conversations || []).map((c) => <Conversation key={c.session_id} c={c} />)}
        {data?.conversations?.length === 0 && <li className="text-slate-500 text-sm" data-testid="website-chats-empty">No website chats yet.</li>}
      </ul>
    </Panel>
  );
};
